/**
 * Install a global fetch wrapper that:
 *  1. Auto-attaches `Authorization: Bearer <token>` from localStorage on every
 *     same-origin and API request (cookie remains primary; this is a fallback
 *     for iOS Safari ITP and Android strict cookie modes).
 *  2. Translates raw network failures (CORS / TLS / DNS) into a clearer message
 *     so the user does not see Safari/Chrome's cryptic "Load failed" string.
 *
 * Call once at app bootstrap (see index.js).
 */

const TOKEN_KEY = 'futureself_auth_token';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const safeGetToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

let installed = false;

/**
 * Named `authFetch` — thin proxy so components can be explicit that the call
 * needs the auth header. The global `installAuthFetch` above already patches
 * window.fetch to attach the Bearer token for our API, so this is functionally
 * equivalent to plain fetch(). Exported so components don't need to remember
 * the "fetch is silently patched" trivia.
 */
export const authFetch = (url, init) => window.fetch(url, init);

export const installAuthFetch = () => {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    // Only attach auth header for OUR backend API. We intentionally do NOT
    // match on `/api/` substring — that would leak the JWT to third-party
    // SDKs (analytics, error trackers, etc.) whose URLs may contain "/api/".
    const url = typeof input === 'string' ? input : input?.url || '';
    const isOurApi = !!API_URL && url.startsWith(`${API_URL}/api`);

    if (isOurApi) {
      const token = safeGetToken();
      const headers = new Headers(init.headers || {});
      if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      // Always send credentials for our API (matches existing per-call behavior)
      init = {
        ...init,
        headers,
        credentials: init.credentials || 'include',
      };
    }

    try {
      return await originalFetch(input, init);
    } catch (err) {
      // Re-throw with a clearer message for absolute network failures.
      if (isOurApi && err && (err.name === 'TypeError' || /Load failed|Failed to fetch/i.test(err.message || ''))) {
        const wrapped = new Error('Network error — please check your connection and try again.');
        wrapped.cause = err;
        wrapped.isNetworkError = true;
        throw wrapped;
      }
      throw err;
    }
  };
};
